import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from '../../components/DatePicker';
import RezervasyonDetayModal from '../../components/RezervasyonDetayModal';
import { apiRequest } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useAlarm } from '../../context/AlarmContext';
import { useKasa } from '../../context/KasaContext';
import UsageInfo from '../../components/UsageInfo';
import { downloadExcel } from '../../utils/exportUtils';
import LoadingSpinner from '../../components/LoadingSpinner';
import Pagination from '../../components/Pagination';

const ODEME_DURUMLARI = ['Farketmez', 'Ödeme alındı', 'Ödeme bekliyor'];

function pad(n) { return String(n).padStart(2, '0'); }
function bugun() { var d = new Date(); return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear(); }
function simdi() { var d = new Date(); return pad(d.getHours()) + ':' + pad(d.getMinutes()); }

function parseLokal(isoStr) {
  if (!isoStr) return null;
  if (isoStr.includes('Z') || isoStr.match(/[+-]\d{2}:\d{2}$/)) return new Date(isoStr);
  var [datePart, timePart] = isoStr.split('T');
  var [y, m, d] = datePart.split('-').map(Number);
  var [h, mi] = (timePart || '00:00').split(':').map(Number);
  return new Date(y, m - 1, d, h, mi);
}

function fmtTarih(isoStr) {
  if (!isoStr) return '';
  var d = parseLokal(isoStr);
  return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear();
}
function fmtSaat(isoStr) {
  if (!isoStr) return '';
  var d = parseLokal(isoStr);
  return pad(d.getHours()) + ':' + pad(d.getMinutes());
}
function fmtPara(n) {
  return (n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

function alarmMetni(tarih) {
  if (!tarih) return null;
  var d = parseLokal(tarih);
  if (!d) return null;
  var bugunD = new Date(); bugunD.setHours(0, 0, 0, 0);
  var alarmGun = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  var fark = Math.round((alarmGun - bugunD) / 86400000);
  var farkStr = fark === 0 ? 'Bugün' : fark > 0 ? '+' + fark + ' gün sonra' : Math.abs(fark) + ' gün önce';
  var saatBilgisi = pad(d.getHours()) + ':' + pad(d.getMinutes());
  return fmtTarih(tarih) + (saatBilgisi && saatBilgisi !== '00:00' ? ' ' + saatBilgisi : '') + '\n' + farkStr;
}

// ── Status Badge ──────────────────────────────────────────────────────────
function StatusBadge({ durum }) {
  const styles = {
    'Ödeme bekliyor': 'bg-rose-50 text-rose-600 border-rose-100',
    'Ödeme alındı': 'bg-emerald-50 text-emerald-600 border-emerald-100',
    'Farketmez': 'bg-gray-50 text-gray-600 border-gray-100'
  };
  return (
    <span className={`text-[9px] font-black px-3 py-1.5 rounded-xl uppercase tracking-widest border transition-all ${styles[durum] || styles['Farketmez']}`}>
      {durum}
    </span>
  );
}

// ── Alarm Modal ───────────────────────────────────────────────────────────
function AlarmModal({ bakiye, onClose, onKaydedildi, onTemizle }) {
  var { tetiklenenIdler, alarmTemizle, toastEkle } = useAlarm();
  var mevcutTarih    = bakiye.alarmTarihi ? fmtTarih(bakiye.alarmTarihi) : bugun();
  var mevcutSaat     = bakiye.alarmTarihi ? fmtSaat(bakiye.alarmTarihi) : '09:00';
  var mevcutAciklama = bakiye.alarmAciklamasi || '';

  var [tarih,        setTarih]        = useState(mevcutTarih);
  var [saat,         setSaat]         = useState(mevcutSaat);
  var [aciklama,     setAciklama]     = useState(mevcutAciklama);
  var [kaydediliyor, setKaydediliyor] = useState(false);
  var [siliniyor,    setSiliniyor]    = useState(false);
  var [hata,         setHata]         = useState('');

  var varMiAlarm = !!bakiye.alarmTarihi;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  async function handleKaydet() {
    setKaydediliyor(true); setHata('');
    try {
      var p = tarih.split('.');
      var isoTarih = p[2] + '-' + p[1].padStart(2,'0') + '-' + p[0].padStart(2,'0') + 'T' + saat + ':00';
      await apiRequest('rezervasyonlar/' + bakiye.rezervasyonId + '/alarm', 'PATCH', {
        alarmTarihi:     isoTarih,
        alarmAciklamasi: aciklama,
      });
      toastEkle({
        tur: 'basari',
        adSoyad: bakiye.adSoyad,
        altMetin: 'Alarm başarıyla kuruldu.',
        yonlendirme: `${tarih} saat ${saat} için hatırlatıcı aktif.`,
        sure: 5000
      });
      onTemizle?.(bakiye.rezervasyonId);
      onKaydedildi?.();
      onClose();
    } catch { setHata('Kayıt sırasında hata oluştu.'); }
    setKaydediliyor(false);
  }

  async function handleSil() {
    setSiliniyor(true); setHata('');
    try {
      await apiRequest('rezervasyonlar/' + bakiye.rezervasyonId + '/alarm', 'PATCH', {
        alarmTarihi:     null,
        alarmAciklamasi: null,
      });
      toastEkle({
        tur: 'bakiye',
        adSoyad: bakiye.adSoyad,
        altMetin: 'Alarm silindi.',
        yonlendirme: 'Hatırlatıcı devre dışı bırakıldı.',
        sure: 4000
      });
      onTemizle?.(bakiye.rezervasyonId);
      onKaydedildi?.();
      onClose();
    } catch { setHata('Silme sırasında hata oluştu.'); }
    setSiliniyor(false);
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[9999] flex items-center justify-center p-4 animate-fadeIn" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-[3rem] shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="bg-gradient-to-br from-indigo-500 to-blue-700 px-8 py-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-2xl">🔔</div>
            <div>
                <h2 className="text-xl font-black text-white tracking-tighter uppercase leading-none">{varMiAlarm ? 'Alarm Düzenle' : 'Alarm Ekle'}</h2>
                <p className="text-white/60 text-[10px] font-black uppercase tracking-widest mt-2">Takip ve Hatırlatma</p>
            </div>
          </div>
          <button onClick={onClose} className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center justify-center font-black transition-all">✕</button>
        </div>

        <div className="p-8 space-y-8">
          <div className="bg-gray-50 rounded-[2rem] p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Misafir Bilgisi</p>
                <span className="text-[10px] font-black text-indigo-500 bg-indigo-50 px-3 py-1 rounded-full uppercase">Oda {bakiye.odaNo}</span>
            </div>
            <p className="text-lg font-black text-gray-900 tracking-tighter leading-none mb-2">{bakiye.adSoyad || '—'}</p>
            <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">
                Kalan: <span className="text-rose-600 font-black">{fmtPara(bakiye.kalanBakiye)} TL</span>
            </p>
          </div>

          {hata && <div className="bg-rose-50 border-2 border-rose-100 rounded-2xl px-6 py-4 text-[10px] font-black text-rose-600 uppercase tracking-widest animate-pulse">⚠️ {hata}</div>}
          
          <div className="space-y-6 text-left">
              <div className="relative group">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-6 bg-white px-2 z-10">Alarm Tarihi</label>
                <DatePicker value={tarih} onChange={setTarih} />
              </div>
              <div className="relative group">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-6 bg-white px-2 z-10">Alarm Saati</label>
                <input type="time" value={saat} onChange={e => setSaat(e.target.value)} 
                    className="w-full border-2 border-gray-100 rounded-2xl px-6 py-4 text-xs font-bold focus:outline-none focus:border-indigo-400 transition-colors shadow-sm" />
              </div>
              <div className="relative group">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-6 bg-white px-2 z-10">Açıklama</label>
                <input value={aciklama} onChange={e => setAciklama(e.target.value)} 
                    placeholder="Örn: Ödeme için ara"
                    className="w-full border-2 border-gray-100 rounded-2xl px-6 py-4 text-xs font-bold focus:outline-none focus:border-indigo-400 transition-colors shadow-sm" />
              </div>
          </div>

          <div className="flex gap-4 pt-4">
            {varMiAlarm && (
              <button onClick={handleSil} disabled={siliniyor}
                className="flex-1 h-14 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95">
                {siliniyor ? '...' : 'Sil'}
              </button>
            )}
            <button onClick={handleKaydet} disabled={kaydediliyor}
              className="flex-[2] h-14 bg-gradient-to-r from-indigo-500 to-blue-700 hover:from-indigo-600 hover:to-blue-800 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-indigo-100 transition-all active:scale-95 leading-none">
              {kaydediliyor ? '...' : '💾 Kaydet'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Firma Ödeme Geçmişi Modal ─────────────────────────────────────────────
function FirmaOdemeGecmisiModal({ grup, onClose, onKaydedildi }) {
  var [odemeler,    setOdemeler]    = useState([]);
  var [yukleniyor,  setYukleniyor]  = useState(true);
  var [siliniyor,   setSiliniyor]   = useState(false);
  var [hata,        setHata]        = useState('');
  var [onayToplam,  setOnayToplam]  = useState(false);

  useEffect(function() {
    document.body.style.overflow = 'hidden';
    return function() { document.body.style.overflow = 'unset'; };
  }, []);

  useEffect(function() {
    async function yukle() {
      setYukleniyor(true);
      try {
        var tumOdemeler = [];
        for (var b of grup.rezervasyonlar) {
          var d = await apiRequest('odemeler/rezervasyon/' + b.rezervasyonId);
          if (d && d.length) {
            d.forEach(function(o) { tumOdemeler.push({ ...o, _adSoyad: b.adSoyad, _odaNo: b.odaNo }); });
          }
        }
        tumOdemeler.sort(function(a, b) { return new Date(b.odemeTarihi) - new Date(a.odemeTarihi); });
        setOdemeler(tumOdemeler);
      } catch { setHata('Ödemeler yüklenemedi.'); }
      setYukleniyor(false);
    }
    yukle();
  }, [grup]);

  async function handleSil(odemeId) {
    setSiliniyor(true);
    try {
      await apiRequest('odemeler/' + odemeId, 'DELETE');
      setOdemeler(function(prev) { return prev.filter(function(o) { return o.odemeId !== odemeId; }); });
      onKaydedildi?.();
    } catch { setHata('Silme başarısız.'); }
    setSiliniyor(false);
  }

  async function handleTumunuSil() {
    setSiliniyor(true); setHata('');
    try {
      for (var o of odemeler) {
        await apiRequest('odemeler/' + o.odemeId, 'DELETE');
      }
      setOdemeler([]);
      onKaydedildi?.();
      setOnayToplam(false);
    } catch { setHata('Toplu silme sırasında hata oluştu.'); }
    setSiliniyor(false);
  }

  var toplamTutar = odemeler.reduce(function(s, o) { return s + (o.tutar || 0); }, 0);

  return createPortal(
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[9999] flex items-center justify-center p-4 animate-fadeIn" onClick={onClose}>
      <div className="bg-[#f8fafc] w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: '92dvh' }} onClick={function(e) { e.stopPropagation(); }}>

        <div className="bg-gradient-to-br from-slate-700 to-slate-900 px-8 py-7 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-2xl">🧾</div>
            <div>
              <h2 className="text-lg font-black text-white tracking-tighter uppercase leading-none">Firma Ödemeleri</h2>
              <p className="text-white/60 text-[10px] font-black uppercase tracking-widest mt-1.5">{grup.firmaAd} · {odemeler.length} kayıt</p>
            </div>
          </div>
          <button onClick={onClose} className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center justify-center font-black transition-all">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-4">
          {hata && <div className="bg-rose-50 border border-rose-100 rounded-2xl px-5 py-3 text-[10px] font-black text-rose-600 uppercase tracking-widest">⚠️ {hata}</div>}

          {yukleniyor ? (
            <div className="py-16 text-center">
              <div className="w-10 h-10 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Yükleniyor...</p>
            </div>
          ) : odemeler.length === 0 ? (
            <div className="py-16 text-center bg-gray-50 rounded-[2rem] border-2 border-dashed border-gray-100">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Ödeme kaydı bulunamadı</p>
            </div>
          ) : (
            <>
              {/* Özet */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl p-4 border border-gray-100 text-center">
                  <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Toplam Alınan</p>
                  <p className="text-xl font-black text-emerald-600 tracking-tighter">{fmtPara(toplamTutar)} <span className="text-xs opacity-60">TL</span></p>
                </div>
                <div className="bg-white rounded-2xl p-4 border border-gray-100 text-center">
                  <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">Kayıt Sayısı</p>
                  <p className="text-xl font-black text-slate-700 tracking-tighter">{odemeler.length}</p>
                </div>
              </div>

              {/* Toplu sil */}
              {!onayToplam ? (
                <button onClick={() => setOnayToplam(true)} disabled={siliniyor}
                  className="w-full py-3 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all border border-rose-100 disabled:opacity-50">
                  🗑 Tüm Ödemeleri Sil ({odemeler.length} kayıt)
                </button>
              ) : (
                <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 space-y-3">
                  <p className="text-[10px] font-black text-rose-700 uppercase tracking-widest text-center">Bu firmaya ait {odemeler.length} ödeme kaydı silinecek. Emin misiniz?</p>
                  <div className="flex gap-3">
                    <button onClick={() => setOnayToplam(false)} className="flex-1 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-gray-200 transition-all">İptal</button>
                    <button onClick={handleTumunuSil} disabled={siliniyor}
                      className="flex-1 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-50 shadow-lg shadow-rose-100">
                      {siliniyor ? 'Siliniyor...' : 'Evet, Hepsini Sil'}
                    </button>
                  </div>
                </div>
              )}

              {/* Ödeme listesi */}
              <div className="space-y-2">
                {odemeler.map(function(o) {
                  return (
                    <div key={o.odemeId} className="bg-white rounded-2xl border border-gray-100 px-5 py-4 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[9px] font-black px-2 py-1 bg-indigo-50 text-indigo-600 rounded-lg uppercase tracking-widest">Oda {o._odaNo}</span>
                          <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{o.odemeTipi}</span>
                        </div>
                        <p className="text-xs font-bold text-gray-600 leading-none truncate">{o._adSoyad}</p>
                        {o.aciklama && <p className="text-[9px] font-bold text-gray-400 mt-0.5 truncate">{o.aciklama}</p>}
                        <p className="text-[9px] font-bold text-gray-300 mt-1">
                          {o.odemeTarihi ? new Date(o.odemeTarihi).toLocaleDateString('tr-TR') : ''}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-black text-emerald-600 tracking-tighter">{fmtPara(o.tutar)} <span className="text-[9px] opacity-60">{o.paraBirimi || 'TL'}</span></p>
                      </div>
                      <button onClick={() => handleSil(o.odemeId)} disabled={siliniyor}
                        className="w-9 h-9 bg-gray-50 hover:bg-rose-50 hover:text-rose-600 text-gray-300 rounded-xl flex items-center justify-center transition-all flex-shrink-0 disabled:opacity-50">
                        🗑
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Ödeme Al Modal ────────────────────────────────────────────────────────
function OdemeAlModal({ bakiye, onClose, onKaydedildi }) {
  var { personel } = useAuth();
  var { toastEkle } = useAlarm();
  var { paraBirimleri, dovizKurlari, fetchVeriler } = useKasa();

  var [aciklama,     setAciklama]     = useState('Rez. ödemesi (Bakiye)');
  var [tutar,        setTutar]        = useState(String(bakiye.kalanBakiye > 0 ? bakiye.kalanBakiye : ''));
  var [paraBirimi,   setParaBirimi]   = useState('TL');
  var [odemeTipi,    setOdemeTipi]    = useState('NAKİT');
  var [bankaAdi,     setBankaAdi]     = useState('');
  var [tarih,        setTarih]        = useState(bugun());
  var [saat,         setSaat]         = useState(simdi());
  var [kaydediliyor, setKaydediliyor] = useState(false);
  var [hata,         setHata]         = useState('');
  var [bankalar,     setBankalar]     = useState([]);
  var [odemeTipleri, setOdemeTipleri] = useState([]);

  useEffect(function() {
    apiRequest('bankatanimlari').then(function(d){ setBankalar(d || []); }).catch(function(){});
    apiRequest('odemetipleri').then(function(d){ 
      var liste = d || [];
      setOdemeTipleri(liste);
      // BUG FIX: Varsayılan ödeme tipini API'den gelen ilk eleman yap (boşta kalmasın)
      if (liste.length > 0) {
        setOdemeTipi(liste[0].ad);
      }
    }).catch(function(){});
  }, []);

  // Modal Scroll Lock
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  var paraBirimiListesi = (paraBirimleri || []).map(function(p){ return p.tcmbKod || p.ad || p; }).filter(Boolean);
  var kurDegeri    = dovizKurlari[paraBirimi] || 1;
  var tlKarsiligi  = paraBirimi !== 'TL' && parseFloat(tutar) > 0 ? (parseFloat(tutar) * kurDegeri).toFixed(2) : null;
  var tutarTL      = parseFloat(tutar) > 0 ? parseFloat(tutar) * kurDegeri : 0;
  var fazlaOdeme   = bakiye.kalanBakiye > 0 && tutarTL > bakiye.kalanBakiye;

  var odemeTipListesi = odemeTipleri.length > 0
    ? odemeTipleri.map(function(o){ return o.ad; })
    : ['NAKİT', 'KREDİ KARTI', 'BANKA HAVALE', 'ÇEK'];

  async function handleKaydet() {
    if (!tutar || parseFloat(tutar) <= 0) { setHata('Geçerli bir tutar giriniz.'); return; }
    setKaydediliyor(true); setHata('');
    try {
      var [g, a, y] = tarih.split('.').map(Number);
      var [sa, dk]  = saat.split(':').map(Number);
      await apiRequest('odemeler', 'POST', {
        aciklama,
        tutar: parseFloat(tutar),
        paraBirimi,
        kurDegeri: paraBirimi === 'TL' ? 1 : kurDegeri,
        odemeTipi,
        bankaAdi,
        odemeTarihi: `${y}-${String(a).padStart(2,'0')}-${String(g).padStart(2,'0')}T${String(sa).padStart(2,'0')}:${String(dk).padStart(2,'0')}:00`,
        odemeSaati: saat,
        rezervasyonId: bakiye.rezervasyonId,
        personelId: personel?.personelId || 1,
      });
      onKaydedildi?.();
      fetchVeriler?.();
      onClose();
    } catch { setHata('Kayıt sırasında hata oluştu.'); }
    setKaydediliyor(false);
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[9999] flex items-center justify-center p-4 animate-fadeIn" onClick={onClose}>
      <div className="bg-[#f8fafc] w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: '96dvh' }} onClick={e => e.stopPropagation()}>
        
        <div className="bg-gradient-to-br from-emerald-500 to-teal-700 px-8 py-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-2xl shadow-xl">💰</div>
            <div>
                <h2 className="text-xl font-black text-white tracking-tighter uppercase leading-none">Ödeme Girişi</h2>
                <p className="text-white/60 text-[10px] font-black uppercase tracking-widest mt-2">Kasa Tahsilat İşlemi</p>
            </div>
          </div>
          <button onClick={onClose} className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center justify-center font-black transition-all">✕</button>
        </div>

        <div className="p-8 space-y-8 overflow-y-auto custom-scrollbar">
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-[2rem] p-6 border border-gray-100 shadow-sm flex flex-col justify-center">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 text-left">Misafir & Oda</p>
                <p className="text-lg font-black text-gray-900 tracking-tighter leading-none mb-1 text-left">{bakiye.adSoyad}</p>
                <p className="text-xs font-bold text-indigo-600 text-left">Oda {bakiye.odaNo} · {bakiye.acenteAd || 'Münferit'}</p>
              </div>
              <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-[2rem] p-6 text-white shadow-xl shadow-rose-100 relative overflow-hidden group">
                <div className="absolute right-[-10px] bottom-[-10px] text-5xl opacity-10 group-hover:scale-110 transition-transform">⏳</div>
                <p className="text-[10px] font-black uppercase tracking-widest opacity-70 mb-1 text-left">Kalan Bakiye</p>
                <p className="text-2xl font-black tracking-tighter text-left">{fmtPara(bakiye.kalanBakiye)} <span className="text-xs uppercase opacity-70">TL</span></p>
              </div>
          </div>

          {hata && <div className="bg-rose-50 border-2 border-rose-100 rounded-2xl px-6 py-4 text-[10px] font-black text-rose-600 uppercase tracking-widest animate-pulse">⚠️ {hata}</div>}
          
          <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-8 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                <div className="relative group md:col-span-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-6 bg-white px-2 z-10">İşlem Açıklaması</label>
                    <input value={aciklama} onChange={e => setAciklama(e.target.value)} 
                        className="w-full border-2 border-gray-50 rounded-2xl px-6 py-4 text-xs font-bold focus:outline-none focus:border-emerald-400 transition-colors bg-gray-50/30 focus:bg-white" />
                </div>

                <div className="relative group">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-6 bg-white px-2 z-10">Tahsilat Tutarı</label>
                    <div className="flex bg-gray-50/30 rounded-2xl border-2 border-gray-50 focus-within:border-emerald-400 focus-within:bg-white transition-all">
                        <input type="number" value={tutar} onChange={e => setTutar(e.target.value)} 
                            className="w-full bg-transparent px-6 py-4 text-sm font-black focus:outline-none" placeholder="0.00" />
                        <select value={paraBirimi} onChange={e => setParaBirimi(e.target.value)} 
                            className="bg-gray-100 border-l border-gray-200 rounded-r-2xl px-4 text-[10px] font-black uppercase tracking-widest focus:outline-none cursor-pointer">
                            <option value="TL">TL</option>
                            {paraBirimiListesi.filter(p => p !== 'TL' && p !== 'TRY').map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                    </div>
                </div>

                <div className="relative group">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-6 bg-white px-2 z-10">Ödeme Tipi</label>
                    <select value={odemeTipi} onChange={e => setOdemeTipi(e.target.value)} 
                        className="w-full border-2 border-gray-50 rounded-2xl px-6 py-4 text-xs font-bold focus:outline-none focus:border-emerald-400 transition-colors bg-gray-50/30 focus:bg-white cursor-pointer appearance-none uppercase tracking-widest">
                        {odemeTipListesi.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>

                {tlKarsiligi && (
                  <div className="md:col-span-2 bg-emerald-50 rounded-[1.5rem] px-6 py-3 border border-emerald-100 flex items-center justify-between">
                    <p className="text-[10px] font-black text-emerald-700 uppercase tracking-widest">TL Karşılığı</p>
                    <p className="text-sm font-black text-emerald-800 tracking-tighter">≈ {fmtPara(tlKarsiligi)} TL <span className="text-[9px] opacity-60 ml-1">(1 {paraBirimi} = {kurDegeri.toFixed(4)} TL)</span></p>
                  </div>
                )}

                {fazlaOdeme && (
                  <div className="md:col-span-2 bg-amber-50 rounded-[1.5rem] px-6 py-3 border border-amber-100 flex items-center gap-3">
                    <span className="text-lg">⚠️</span>
                    <p className="text-[10px] font-black text-amber-700 uppercase tracking-widest leading-relaxed">Bu ödeme kalan bakiyeyi aşıyor!</p>
                  </div>
                )}

                {(odemeTipi.toLocaleLowerCase('tr').includes('kredi') || odemeTipi.toLocaleLowerCase('tr').includes('banka') || odemeTipi.toLocaleLowerCase('tr').includes('havale')) && (
                  <div className="md:col-span-2 relative group animate-fadeIn">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-6 bg-white px-2 z-10">Banka Seçimi</label>
                    <select value={bankaAdi} onChange={e => setBankaAdi(e.target.value)} 
                        className="w-full border-2 border-gray-50 rounded-2xl px-6 py-4 text-xs font-bold focus:outline-none focus:border-emerald-400 transition-colors bg-gray-50/30 focus:bg-white cursor-pointer appearance-none">
                        <option value="">Banka Seçiniz</option>
                        {bankalar.map(b => <option key={b.bankaTanimiId} value={b.ad}>{b.ad}</option>)}
                    </select>
                  </div>
                )}

                <div className="relative group">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-6 bg-white px-2 z-10">Tahsilat Tarihi</label>
                    <DatePicker value={tarih} onChange={setTarih} />
                </div>
                <div className="relative group">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-6 bg-white px-2 z-10">Saat</label>
                    <input type="time" value={saat} onChange={e => setSaat(e.target.value)} 
                        className="w-full border-2 border-gray-50 rounded-2xl px-6 py-4 text-xs font-bold focus:outline-none focus:border-emerald-400 transition-colors bg-gray-50/30 focus:bg-white" />
                </div>
            </div>
          </div>

          <button onClick={handleKaydet} disabled={kaydediliyor}
            className="w-full h-16 bg-gradient-to-r from-emerald-500 to-teal-700 hover:from-emerald-600 hover:to-teal-800 text-white rounded-[1.5rem] text-xs font-black uppercase tracking-[0.2em] shadow-xl shadow-emerald-100 transition-all active:scale-[0.98] leading-none mb-4">
            {kaydediliyor ? 'İşlem Yapılıyor...' : '💳 KASAYA EKLE — TAHSİLAT YAP'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function CheckOutUyariModal({ onClose }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  return createPortal(
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[9999] flex items-center justify-center p-4 animate-fadeIn" onClick={onClose}>
      <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-sm p-10 flex flex-col items-center gap-6 text-center relative overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="w-20 h-20 rounded-full bg-indigo-50 flex items-center justify-center text-4xl shadow-inner relative z-10">ℹ️</div>
        <div className="relative z-10">
            <p className="text-xl font-black text-gray-900 uppercase tracking-tighter mb-2">Check OUT Yapılmış</p>
            <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest leading-relaxed">Bu rezervasyonun işlemleri kapandığı için yeni işlem yapılamaz.</p>
        </div>
        <button onClick={onClose} className="w-full h-14 bg-gray-900 hover:bg-black text-white rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-xl transition-all active:scale-95 leading-none relative z-10">Anladım</button>
      </div>
    </div>,
    document.body
  );
}

function SilOnayModal({ onClose, onSil }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  return createPortal(
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[9999] flex items-center justify-center p-4 animate-fadeIn" onClick={onClose}>
      <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-sm p-10 flex flex-col items-center gap-6 text-center relative overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="w-20 h-20 rounded-full bg-rose-50 flex items-center justify-center text-4xl shadow-inner relative z-10">⚠️</div>
        <div className="relative z-10">
            <p className="text-xl font-black text-gray-900 uppercase tracking-tighter mb-2">Emin misiniz?</p>
            <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest leading-relaxed">Bu kayıt kalıcı olarak silinecektir. Bu işlem geri alınamaz.</p>
        </div>
        <div className="flex gap-4 w-full relative z-10">
          <button onClick={onClose} className="flex-1 h-14 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all">İptal</button>
          <button onClick={onSil}   className="flex-1 h-14 bg-rose-600 hover:bg-rose-700 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-xl shadow-rose-100 transition-all active:scale-95">Sil</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Firma Toplu Ödeme Modal ───────────────────────────────────────────────
function FirmaOdemeModal({ grup, onClose, onKaydedildi }) {
  var { personel } = useAuth();
  var { paraBirimleri, dovizKurlari, fetchVeriler } = useKasa();

  var grupToplam = grup.rezervasyonlar.reduce(function(s, b) { return s + (b.kalanBakiye || 0); }, 0);

  var [tutar,        setTutar]        = useState(String(grupToplam > 0 ? grupToplam.toFixed(2) : ''));
  var [paraBirimi,   setParaBirimi]   = useState('TL');
  var [odemeTipi,    setOdemeTipi]    = useState('NAKİT');
  var [bankaAdi,     setBankaAdi]     = useState('');
  var [tarih,        setTarih]        = useState(bugun());
  var [saat,         setSaat]         = useState(simdi());
  var [aciklama,     setAciklama]     = useState('Firma ödemesi');
  var [kaydediliyor, setKaydediliyor] = useState(false);
  var [hata,         setHata]         = useState('');
  var [bankalar,     setBankalar]     = useState([]);
  var [odemeTipleri, setOdemeTipleri] = useState([]);

  useEffect(function() {
    document.body.style.overflow = 'hidden';
    return function() { document.body.style.overflow = 'unset'; };
  }, []);

  useEffect(function() {
    apiRequest('bankatanimlari').then(function(d) { setBankalar(d || []); }).catch(function() {});
    apiRequest('odemetipleri').then(function(d) {
      var liste = d || [];
      setOdemeTipleri(liste);
      if (liste.length > 0) setOdemeTipi(liste[0].ad);
    }).catch(function() {});
  }, []);

  var paraBirimiListesi = (paraBirimleri || []).map(function(p) { return p.tcmbKod || p.ad || p; }).filter(Boolean);
  var kurDegeri = dovizKurlari[paraBirimi] || 1;
  var tutarTL = parseFloat(tutar) > 0 ? parseFloat(tutar) * kurDegeri : 0;
  var odemeTipListesi = odemeTipleri.length > 0 ? odemeTipleri.map(function(o) { return o.ad; }) : ['NAKİT', 'KREDİ KARTI', 'BANKA HAVALE'];

  async function handleKaydet() {
    if (!tutar || parseFloat(tutar) <= 0) { setHata('Geçerli bir tutar giriniz.'); return; }
    setKaydediliyor(true); setHata('');
    try {
      var [g, a, y] = tarih.split('.').map(Number);
      var [sa, dk]  = saat.split(':').map(Number);
      var odemeTarihi = y + '-' + String(a).padStart(2,'0') + '-' + String(g).padStart(2,'0') + 'T' + String(sa).padStart(2,'0') + ':' + String(dk).padStart(2,'0') + ':00';

      var aktifRezler = grup.rezervasyonlar.filter(function(b) { return b.kalanBakiye > 0; });
      if (aktifRezler.length === 0) aktifRezler = grup.rezervasyonlar;
      var toplamKalan = aktifRezler.reduce(function(s, b) { return s + (b.kalanBakiye || 0); }, 0);

      for (var b of aktifRezler) {
        var oran = toplamKalan > 0 ? (b.kalanBakiye || 0) / toplamKalan : 1 / aktifRezler.length;
        var rezTutarTL = parseFloat((tutarTL * oran).toFixed(4));
        if (rezTutarTL <= 0) continue;
        var postTutar = paraBirimi !== 'TL' ? parseFloat((rezTutarTL / kurDegeri).toFixed(4)) : rezTutarTL;
        await apiRequest('odemeler', 'POST', {
          aciklama: aciklama + ' (' + grup.firmaAd + ')',
          tutar: postTutar,
          paraBirimi,
          kurDegeri: paraBirimi === 'TL' ? 1 : kurDegeri,
          odemeTipi,
          bankaAdi,
          odemeTarihi,
          odemeSaati: saat,
          rezervasyonId: b.rezervasyonId,
          personelId: personel?.personelId || 1,
        });
      }
      onKaydedildi?.();
      fetchVeriler?.();
      onClose();
    } catch { setHata('Kayıt sırasında hata oluştu.'); }
    setKaydediliyor(false);
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[9999] flex items-center justify-center p-4 animate-fadeIn" onClick={onClose}>
      <div className="bg-[#f8fafc] w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden flex flex-col" style={{ maxHeight: '96dvh' }} onClick={function(e) { e.stopPropagation(); }}>

        <div className="bg-gradient-to-br from-violet-600 to-indigo-700 px-8 py-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-2xl shadow-xl">🏢</div>
            <div>
              <h2 className="text-xl font-black text-white tracking-tighter uppercase leading-none">Firma Ödemesi</h2>
              <p className="text-white/60 text-[10px] font-black uppercase tracking-widest mt-2">{grup.firmaAd}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center justify-center font-black transition-all">✕</button>
        </div>

        <div className="p-8 space-y-8 overflow-y-auto custom-scrollbar">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-[2rem] p-6 border border-gray-100 shadow-sm">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 text-left">Firma Özeti</p>
              <p className="text-lg font-black text-gray-900 tracking-tighter leading-none mb-1 text-left">{grup.firmaAd}</p>
              <p className="text-xs font-bold text-violet-600 text-left">{grup.rezervasyonlar.length} rezervasyon · Orantılı dağıtım</p>
            </div>
            <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-[2rem] p-6 text-white shadow-xl shadow-rose-100 relative overflow-hidden group">
              <div className="absolute right-[-10px] bottom-[-10px] text-5xl opacity-10 group-hover:scale-110 transition-transform">⏳</div>
              <p className="text-[10px] font-black uppercase tracking-widest opacity-70 mb-1 text-left">Toplam Kalan Bakiye</p>
              <p className="text-2xl font-black tracking-tighter text-left">{fmtPara(grupToplam)} <span className="text-xs uppercase opacity-70">TL</span></p>
            </div>
          </div>

          {hata && <div className="bg-rose-50 border-2 border-rose-100 rounded-2xl px-6 py-4 text-[10px] font-black text-rose-600 uppercase tracking-widest animate-pulse">⚠️ {hata}</div>}

          <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-8 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <div className="relative group md:col-span-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-6 bg-white px-2 z-10">İşlem Açıklaması</label>
                <input value={aciklama} onChange={function(e) { setAciklama(e.target.value); }}
                  className="w-full border-2 border-gray-50 rounded-2xl px-6 py-4 text-xs font-bold focus:outline-none focus:border-violet-400 transition-colors bg-gray-50/30 focus:bg-white" />
              </div>

              <div className="relative group">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-6 bg-white px-2 z-10">Tahsilat Tutarı</label>
                <div className="flex bg-gray-50/30 rounded-2xl border-2 border-gray-50 focus-within:border-violet-400 focus-within:bg-white transition-all">
                  <input type="number" value={tutar} onChange={function(e) { setTutar(e.target.value); }}
                    className="w-full bg-transparent px-6 py-4 text-sm font-black focus:outline-none" placeholder="0.00" />
                  <select value={paraBirimi} onChange={function(e) { setParaBirimi(e.target.value); }}
                    className="bg-gray-100 border-l border-gray-200 rounded-r-2xl px-4 text-[10px] font-black uppercase tracking-widest focus:outline-none cursor-pointer">
                    <option value="TL">TL</option>
                    {paraBirimiListesi.filter(function(p) { return p !== 'TL' && p !== 'TRY'; }).map(function(p) { return <option key={p} value={p}>{p}</option>; })}
                  </select>
                </div>
              </div>

              <div className="relative group">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-6 bg-white px-2 z-10">Ödeme Tipi</label>
                <select value={odemeTipi} onChange={function(e) { setOdemeTipi(e.target.value); }}
                  className="w-full border-2 border-gray-50 rounded-2xl px-6 py-4 text-xs font-bold focus:outline-none focus:border-violet-400 transition-colors bg-gray-50/30 focus:bg-white cursor-pointer appearance-none uppercase tracking-widest">
                  {odemeTipListesi.map(function(t) { return <option key={t} value={t}>{t}</option>; })}
                </select>
              </div>

              {(odemeTipi.toLocaleLowerCase('tr').includes('kredi') || odemeTipi.toLocaleLowerCase('tr').includes('banka') || odemeTipi.toLocaleLowerCase('tr').includes('havale')) && (
                <div className="md:col-span-2 relative group animate-fadeIn">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-6 bg-white px-2 z-10">Banka Seçimi</label>
                  <select value={bankaAdi} onChange={function(e) { setBankaAdi(e.target.value); }}
                    className="w-full border-2 border-gray-50 rounded-2xl px-6 py-4 text-xs font-bold focus:outline-none focus:border-violet-400 transition-colors bg-gray-50/30 focus:bg-white cursor-pointer appearance-none">
                    <option value="">Banka Seçiniz</option>
                    {bankalar.map(function(b) { return <option key={b.bankaTanimiId} value={b.ad}>{b.ad}</option>; })}
                  </select>
                </div>
              )}

              <div className="relative group">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-6 bg-white px-2 z-10">Tahsilat Tarihi</label>
                <DatePicker value={tarih} onChange={setTarih} />
              </div>
              <div className="relative group">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-6 bg-white px-2 z-10">Saat</label>
                <input type="time" value={saat} onChange={function(e) { setSaat(e.target.value); }}
                  className="w-full border-2 border-gray-50 rounded-2xl px-6 py-4 text-xs font-bold focus:outline-none focus:border-violet-400 transition-colors bg-gray-50/30 focus:bg-white" />
              </div>
            </div>
          </div>

          <div className="bg-violet-50 border border-violet-100 rounded-2xl px-6 py-4 text-[10px] font-bold text-violet-700 leading-relaxed">
            💡 Girilen tutar, firma grubundaki tüm rezervasyonların kalan bakiyelerine orantılı olarak dağıtılır.
          </div>

          <button onClick={handleKaydet} disabled={kaydediliyor}
            className="w-full h-16 bg-gradient-to-r from-violet-600 to-indigo-700 hover:from-violet-700 hover:to-indigo-800 text-white rounded-[1.5rem] text-xs font-black uppercase tracking-[0.2em] shadow-xl shadow-violet-100 transition-all active:scale-[0.98] leading-none mb-4">
            {kaydediliyor ? 'İşlem Yapılıyor...' : '💳 FİRMA ÖDEMESİNİ KASAYA EKLE'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Mobil kart bileşeni ───────────────────────────────────────────────────
function BakiyeKart({ b, tetiklendi, onOdeme, onDetay, onSil, onAlarm }) {
  var alarmVar = !!b.alarmMetniHesap;
  return (
    <div className={`bg-white rounded-[2rem] border-2 shadow-sm p-6 space-y-5 transition-all animate-fadeIn ${tetiklendi ? 'border-orange-400 bg-orange-50/50' : 'border-gray-50'}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[9px] font-black text-indigo-500 uppercase tracking-widest mb-1.5 px-2 bg-indigo-50 rounded-full inline-block">ODA {b.odaNo}</p>
          <p className="text-base font-black text-gray-900 tracking-tighter leading-none">{b.adSoyad || '—'}</p>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">{b.acenteAd || 'Münferit'}</p>
        </div>
        <div className="text-right">
          <p className={`text-lg font-black tracking-tighter ${b.kalanBakiye > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
            {fmtPara(b.kalanBakiye)} <span className="text-[10px] uppercase opacity-70">TL</span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-2xl p-4">
        <div>
          <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1">Giriş</p>
          <p className="text-[10px] font-black text-gray-700 tracking-tight leading-none italic">{fmtTarih(b.girisTarihi)} {b.girisSaati}</p>
        </div>
        <div>
          <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1">Çıkış</p>
          <p className="text-[10px] font-black text-gray-700 tracking-tight leading-none italic">
            {!b.cikisTarihi || b.cikisTarihi.startsWith('0001') ? 'BELİRSİZ / AÇIK' : fmtTarih(b.cikisTarihi) + ' ' + b.cikisSaati}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <StatusBadge durum={b.odemeDurumu} />
        <button
          onClick={() => onAlarm(b)}
          className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest px-3 py-2 rounded-xl transition-all ${
            tetiklendi ? 'bg-orange-500 text-white shadow-lg animate-pulse' : alarmVar ? 'bg-indigo-50 text-indigo-600' : 'bg-gray-50 text-gray-400'
          }`}
        >
          <span>🔔</span>
          {tetiklendi ? 'Tetiklendi!' : alarmVar ? 'Alarm Var' : 'Ekle'}
        </button>
      </div>

      <div className="flex gap-2 pt-2">
        {onOdeme && (
          <button onClick={() => onOdeme(b)}
            className="flex-[2] h-12 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl shadow-lg shadow-emerald-100 transition-all active:scale-95 leading-none">
            Ödeme Al
          </button>
        )}
        <button onClick={() => onDetay(b)}
          className="flex-1 h-12 bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all active:scale-95 leading-none">
          Detay
        </button>
        <button onClick={() => onSil(b.rezervasyonId)}
          className="w-12 h-12 bg-gray-100 text-gray-400 rounded-xl flex items-center justify-center transition-all active:scale-95 text-xl">
          🗑
        </button>
      </div>
    </div>
  );
}

// ── Ana Bileşen ───────────────────────────────────────────────────────────
export default function Bakiyeler() {
  var { tetiklenenIdler, alarmTemizle } = useAlarm();

  var [bakiyeler,    setBakiyeler]    = useState([]);
  var [yukleniyor,   setYukleniyor]   = useState(true);

  var [odemeDurum,   setOdemeDurum]   = useState('Ödeme bekliyor');
  var [acente,       setAcente]       = useState('Farketmez');
  var [firma,        setFirma]        = useState('Farketmez');
  var [aktifOdeme,   setAktifOdeme]   = useState('Ödeme bekliyor');
  var [aktifAcente,  setAktifAcente]  = useState('Farketmez');
  var [aktifFirma,   setAktifFirma]   = useState('Farketmez');
  var [sortKey,      setSortKey]      = useState('');
  var [sortDir,      setSortDir]      = useState('asc');
  var [kapaliGruplar, setKapaliGruplar] = useState(new Set());

  var [odemeModal,           setOdemeModal]           = useState(null);
  var [firmaOdemeModal,      setFirmaOdemeModal]      = useState(null);
  var [firmaOdemeGecmisi,    setFirmaOdemeGecmisi]    = useState(null);
  var [detayId,         setDetayId]         = useState(null);
  var [uyariModal,      setUyariModal]      = useState(false);
  var [silModal,     setSilModal]     = useState(null);
  var [alarmModal,   setAlarmModal]   = useState(null);
  var [aktifSayfa,   setAktifSayfa]   = useState(1);
  var [sayfaBasi,    setSayfaBasi]    = useState(100);

  const fetchBakiyeler = useCallback(async function() {
    setYukleniyor(true);
    try {
      var data = await apiRequest('rezervasyonlar/bakiyeler');
      var liste = (data || []).map(function(b) {
        return { ...b, alarmMetniHesap: b.alarmTarihi ? alarmMetni(b.alarmTarihi) : null };
      });
      setBakiyeler(liste);
    } catch (e) { console.error('Bakiyeler yüklenemedi:', e); }
    setYukleniyor(false);
  }, []);

  useEffect(function() { fetchBakiyeler(); }, [fetchBakiyeler]);

  useEffect(function() {
    var firmaIdler = new Set(bakiyeler.filter(function(b){ return !!b.firmaId; }).map(function(b){ return b.firmaId; }));
    setKapaliGruplar(firmaIdler);
  }, [bakiyeler]);

  function handleSort(key) {
    if (sortKey === key) setSortDir(function(d){ return d === 'asc' ? 'desc' : 'asc'; });
    else { setSortKey(key); setSortDir('asc'); }
  }

  function filtrele() { setAktifOdeme(odemeDurum); setAktifAcente(acente); setAktifFirma(firma); setAktifSayfa(1); }
  function temizle()  {
    setOdemeDurum('Farketmez'); setAcente('Farketmez'); setFirma('Farketmez');
    setAktifOdeme('Farketmez'); setAktifAcente('Farketmez'); setAktifFirma('Farketmez'); setAktifSayfa(1);
  }

  var acenteler = useMemo(function() {
    return [...new Set(bakiyeler.map(function(b){ return b.acenteAd; }).filter(Boolean))].sort(function(a,b){ return a.localeCompare(b,'tr'); });
  }, [bakiyeler]);
  var firmalar = useMemo(function() {
    return [...new Set(bakiyeler.filter(function(b){ return !!b.firmaId; }).map(function(b){ return b.firmaAd; }).filter(Boolean))].sort(function(a,b){ return a.localeCompare(b,'tr'); });
  }, [bakiyeler]);

  function toggleGrup(firmaId) {
    setKapaliGruplar(function(prev) {
      var next = new Set(prev);
      if (next.has(firmaId)) next.delete(firmaId); else next.add(firmaId);
      return next;
    });
  }

  var filtrelenmis = useMemo(function() {
    var liste = bakiyeler.filter(function(b) {
      if (aktifOdeme !== 'Farketmez' && b.odemeDurumu !== aktifOdeme) return false;
      if (aktifAcente !== 'Farketmez' && b.acenteAd !== aktifAcente) return false;
      if (aktifFirma !== 'Farketmez' && b.firmaAd !== aktifFirma) return false;
      return true;
    });
    if (!sortKey) return liste;
    return liste.slice().sort(function(a, b) {
      var va = a[sortKey], vb = b[sortKey];
      if (typeof va === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc'
        ? String(va || '').localeCompare(String(vb || ''), 'tr', { numeric: true })
        : String(vb || '').localeCompare(String(va || ''), 'tr', { numeric: true });
    });
  }, [bakiyeler, aktifOdeme, aktifAcente, aktifFirma, sortKey, sortDir]);

  var toplamBakiye = filtrelenmis.reduce(function(s, b){ return s + (b.kalanBakiye || 0); }, 0);
  var bekleyenTahsilat = bakiyeler.filter(b => b.odemeDurumu === 'Ödeme bekliyor').length;
  var aktifAlarmli = bakiyeler.filter(b => b.alarmTarihi).length;

  var sayfaData = filtrelenmis.slice((aktifSayfa - 1) * sayfaBasi, aktifSayfa * sayfaBasi);
  var normalBakiyeler = sayfaData.filter(b => !b.firmaId);
  var firmaBakiyeler  = sayfaData.filter(b => !!b.firmaId);

  // Sayfalamanın dışında: ödeme geçmişi modalı için firmanın TÜM rezervasyonları
  function getFirmaTumRezervasyonlar(firmaId) {
    var tumu = filtrelenmis.filter(function(b) { return b.firmaId === firmaId; });
    return { firmaId, firmaAd: tumu[0]?.firmaAd || ('Firma #' + firmaId), rezervasyonlar: tumu };
  }
  // Firma bazlı grupla: firmaId → { firmaAd, rezervasyonlar[] }
  var firmaGruplari = useMemo(function() {
    var map = {};
    firmaBakiyeler.forEach(function(b) {
      var key = b.firmaId;
      if (!map[key]) map[key] = { firmaId: b.firmaId, firmaAd: b.firmaAd || ('Firma #' + b.firmaId), rezervasyonlar: [] };
      map[key].rezervasyonlar.push(b);
    });
    return Object.values(map).sort((a,b) => (a.firmaAd || '').localeCompare(b.firmaAd || '', 'tr'));
  }, [firmaBakiyeler]);

  var COLS = [
    { key: 'adSoyad',     label: 'Misafir Adı', col: 'flex-[2]'   },
    { key: 'odaNo',       label: 'Oda',      col: 'w-24 text-center' },
    { key: 'acenteAd',    label: 'Acente',   col: 'flex-1 hidden lg:block' },
    { key: 'cikisTarihi', label: 'Çıkış',    col: 'w-32' },
    { key: 'kalanBakiye', label: 'Bakiye',   col: 'w-36 text-right' },
    { key: 'odemeDurumu', label: 'Durum',    col: 'w-40 text-center' },
    { key: 'alarm',       label: 'Takip',    col: 'w-32 text-center', noSort: true },
    { key: 'aksiyon',     label: 'İşlem',    col: 'w-48 text-right', noSort: true }
  ];

  function handleDetay(b) {
    if (b.checkOutYapildiMi) setUyariModal(true);
    else setDetayId(b.rezervasyonId);
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] p-4 lg:p-8 space-y-10 animate-fadeIn">
      
      {/* Dashboard Metrics Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8 mb-12">
          <div className="text-left">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-blue-700 rounded-[1.5rem] flex items-center justify-center text-2xl shadow-xl shadow-indigo-100">⚖️</div>
                <h1 className="text-3xl font-black text-gray-900 tracking-tighter uppercase leading-none">Bakiye Durumu</h1>
              </div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] ml-1">Finansal Takip ve Tahsilat Merkezi</p>
          </div>

          <div className="flex flex-col items-end gap-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 lg:flex">
              {[
                  { label: 'TOPLAM BAKİYE', val: fmtPara(toplamBakiye), icon: '💰', color: 'from-rose-500 to-rose-600', shadow: 'shadow-rose-100' },
                  { label: 'BEKLEYEN', val: bekleyenTahsilat, icon: '⏳', color: 'from-orange-400 to-orange-500', shadow: 'shadow-orange-100' },
                  { label: 'ALARMLAR', val: aktifAlarmli, icon: '🔔', color: 'from-blue-500 to-indigo-600', shadow: 'shadow-blue-100' }
              ].map(k => (
                  <div key={k.label} className={`bg-gradient-to-br ${k.color} rounded-[2rem] p-5 text-white shadow-xl ${k.shadow} relative overflow-hidden group min-w-[160px]`}>
                      <div className="absolute right-[-10px] bottom-[-10px] text-4xl opacity-10 group-hover:scale-125 transition-transform">{k.icon}</div>
                      <p className="text-[9px] font-black uppercase tracking-widest opacity-70 mb-1 text-left">{k.label}</p>
                      <p className="text-xl font-black tracking-tighter text-left">{k.val}{k.label === 'TOPLAM BAKİYE' && <span className="text-[10px] opacity-70 ml-1">TL</span>}</p>
                  </div>
              ))}
            </div>
            <button
              onClick={() => downloadExcel('bakiye-durumu', 'Bakiyeler',
                ['Misafir', 'Oda', 'Firma', 'Acente', 'Çıkış Tarihi', 'Oda Tutarı (TL)', 'Kalan Bakiye (TL)', 'Durum'],
                filtrelenmis.map(b => [
                  b.adSoyad, b.odaNo || '—', b.firmaAd || '—', b.acenteAd || '—',
                  b.cikisTarihi ? new Date(b.cikisTarihi).toLocaleDateString('tr-TR') : 'Belirsiz',
                  (b.odaTutari || 0).toFixed(4),
                  (b.kalanBakiye || 0).toFixed(4),
                  b.odemeDurumu || '—'
                ])
              )}
              className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-2xl text-[10px] font-black shadow-lg shadow-emerald-100 transition-all hover:-translate-y-0.5 active:scale-95 uppercase tracking-widest">
              <span className="text-base">📊</span>
              <span>EXCEL</span>
            </button>
          </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-8 py-8 bg-[#f8fafc]/30 flex flex-col md:flex-row gap-8 items-end">
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-6 w-full text-left">
                  <div className="relative group">
                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-6 bg-white px-2 z-10 transition-colors group-focus-within:text-indigo-500">Ödeme Durumu</label>
                    <select value={odemeDurum} onChange={e => setOdemeDurum(e.target.value)}
                        className="w-full border-2 border-gray-100 rounded-2xl px-6 py-4 text-[10px] font-black uppercase tracking-widest bg-white focus:outline-none focus:border-indigo-400 transition-colors shadow-sm cursor-pointer appearance-none">
                        {ODEME_DURUMLARI.map(d => <option key={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="relative group">
                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-6 bg-white px-2 z-10 transition-colors group-focus-within:text-indigo-500">Acente</label>
                    <select value={acente} onChange={e => setAcente(e.target.value)}
                        className="w-full border-2 border-gray-100 rounded-2xl px-6 py-4 text-[10px] font-black uppercase tracking-widest bg-white focus:outline-none focus:border-indigo-400 transition-colors shadow-sm cursor-pointer appearance-none">
                        <option>Farketmez</option>
                        {acenteler.map(a => <option key={a}>{a}</option>)}
                    </select>
                  </div>
                  <div className="relative group">
                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest absolute -top-2 left-6 bg-white px-2 z-10 transition-colors group-focus-within:text-indigo-500">Firma</label>
                    <select value={firma} onChange={e => setFirma(e.target.value)}
                        className="w-full border-2 border-gray-100 rounded-2xl px-6 py-4 text-[10px] font-black uppercase tracking-widest bg-white focus:outline-none focus:border-indigo-400 transition-colors shadow-sm cursor-pointer appearance-none">
                        <option>Farketmez</option>
                        {firmalar.map(f => <option key={f}>{f}</option>)}
                    </select>
                  </div>
              </div>
              <div className="flex gap-3 w-full md:w-auto">
                <button onClick={filtrele} className="flex-1 md:w-32 h-14 bg-gray-900 hover:bg-black text-white rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 leading-none">Filtrele</button>
                <button onClick={temizle} className="flex-1 md:w-32 h-14 bg-gray-100 hover:bg-gray-200 text-gray-500 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 leading-none">Temizle</button>
              </div>
          </div>

          <div className="p-8">
              {/* Desktop View */}
              <div className="hidden md:block overflow-hidden">
                  <div className="flex items-center px-6 py-4 bg-gray-50/50 rounded-2xl mb-4 border border-gray-50">
                      {COLS.map(col => (
                          <div key={col.key} 
                               onClick={() => !col.noSort && handleSort(col.key)}
                               className={`${col.col} text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2 ${col.noSort ? '' : 'cursor-pointer hover:text-indigo-500 transition-colors'}`}>
                              {col.label}
                              {!col.noSort && (sortKey === col.key
                                ? <span className="text-indigo-500">{sortDir === 'asc' ? '↑' : '↓'}</span>
                                : <span className="text-gray-200 opacity-0 group-hover:opacity-100">⇅</span>
                              )}
                          </div>
                      ))}
                  </div>

                  {yukleniyor ? (
                      <LoadingSpinner fullPage text="Veriler Yükleniyor..." color="indigo" size="lg" />
                  ) : filtrelenmis.length === 0 ? (
                      <div className="py-20 text-center bg-gray-50 rounded-[2rem] border-2 border-dashed border-gray-100">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Kayıt Bulunamadı</p>
                      </div>
                  ) : (
                    <>
                      {/* ── Bireysel Rezervasyonlar ── */}
                      {normalBakiyeler.length > 0 && (
                        <div className="space-y-3">
                          {firmaGruplari.length > 0 && (
                            <div className="flex items-center gap-3 px-2 pt-2 pb-1">
                              <div className="h-px flex-1 bg-slate-100" />
                              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Bireysel Rezervasyonlar</span>
                              <div className="h-px flex-1 bg-slate-100" />
                            </div>
                          )}
                          {normalBakiyeler.map(b => {
                            var tetiklendi = tetiklenenIdler?.has(b.rezervasyonId);
                            return (
                              <div key={b.rezervasyonId} className={`flex items-center px-6 py-5 rounded-[2rem] border transition-all group ${tetiklendi ? 'bg-orange-50 border-orange-200 shadow-lg shadow-orange-50' : 'bg-white border-transparent hover:border-gray-100 hover:bg-gray-50/50 hover:shadow-sm'}`}>
                                <div className="flex-[2] text-left">
                                  <p className="text-sm font-black text-gray-900 tracking-tighter leading-none mb-1">{b.adSoyad || '—'}</p>
                                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.1em]">{b.acenteAd || 'Münferit'}</p>
                                </div>
                                <div className="w-24 text-center">
                                  <span className="text-[10px] font-black text-indigo-500 bg-indigo-50 px-3 py-1.5 rounded-xl uppercase tracking-widest">{b.odaNo}</span>
                                </div>
                                <div className="flex-1 hidden lg:block text-left italic text-[10px] font-bold text-gray-400">{b.acenteAd || 'Münferit'}</div>
                                <div className="w-32 text-left">
                                  <p className="text-[10px] font-black text-gray-700 tracking-tight leading-none italic">
                                    {!b.cikisTarihi || b.cikisTarihi.startsWith('0001') ? 'BELİRSİZ / AÇIK' : fmtTarih(b.cikisTarihi)}
                                  </p>
                                  <p className="text-[8px] font-bold text-gray-400 uppercase mt-1">Check-OUT Tarihi</p>
                                </div>
                                <div className="w-36 text-right">
                                  <p className={`text-sm font-black tracking-tighter ${b.kalanBakiye > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{fmtPara(b.kalanBakiye)} TL</p>
                                </div>
                                <div className="w-40 text-center"><StatusBadge durum={b.odemeDurumu} /></div>
                                <div className="w-32 flex justify-center">
                                  <button onClick={() => setAlarmModal(b)} className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${tetiklendi ? 'bg-orange-500 text-white shadow-lg animate-pulse' : b.alarmTarihi ? 'bg-indigo-50 text-indigo-500 hover:bg-indigo-100' : 'bg-gray-50 text-gray-300 hover:bg-indigo-50 hover:text-indigo-400'}`}>🔔</button>
                                </div>
                                <div className="w-48 flex justify-end gap-2 pr-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button onClick={() => setOdemeModal(b)} className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-[9px] font-black uppercase tracking-widest transition-all active:scale-95 leading-none">Ödeme Al</button>
                                  <button onClick={() => handleDetay(b)} className="w-9 h-9 bg-gray-900 hover:bg-black text-white rounded-xl flex items-center justify-center transition-all active:scale-95">📄</button>
                                  <button onClick={() => setSilModal(b.rezervasyonId)} className="w-9 h-9 bg-gray-100 hover:bg-rose-50 hover:text-rose-600 text-gray-400 rounded-xl flex items-center justify-center transition-all active:scale-95 italic font-black">🗑</button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* ── Kurumsal Rezervasyonlar ── */}
                      {firmaGruplari.length > 0 && (
                        <div className="space-y-6 mt-6">
                          <div className="flex items-center gap-3 px-2">
                            <div className="h-px flex-1 bg-violet-100" />
                            <span className="text-[10px] font-black text-violet-500 uppercase tracking-widest">Kurumsal Rezervasyonlar</span>
                            <div className="h-px flex-1 bg-violet-100" />
                          </div>
                          {firmaGruplari.map(grup => {
                            var grupToplam = grup.rezervasyonlar.reduce((s,b) => s+(b.kalanBakiye||0), 0);
                            var grupKapali = kapaliGruplar.has(grup.firmaId);
                            return (
                              <div key={grup.firmaId} className="border-2 border-violet-100 rounded-[2rem] overflow-hidden">
                                <div className="bg-violet-50 flex items-center">
                                  <button onClick={() => toggleGrup(grup.firmaId)} className="flex-1 px-6 py-4 flex items-center justify-between hover:bg-violet-100 transition-colors">
                                    <div className="flex items-center gap-3">
                                      <span className="text-lg">🏢</span>
                                      <div className="text-left">
                                        <p className="text-sm font-black text-violet-800 uppercase tracking-tight">{grup.firmaAd}</p>
                                        <p className="text-[9px] font-bold text-violet-400 uppercase tracking-widest">{grup.rezervasyonlar.length} rezervasyon</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                      <div className="text-right">
                                        <p className={`text-base font-black tracking-tighter ${grupToplam > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{fmtPara(grupToplam)} TL</p>
                                        <p className="text-[9px] font-bold text-violet-400 uppercase tracking-widest">Toplam Bakiye</p>
                                      </div>
                                      <span className="text-violet-400 font-black text-lg">{grupKapali ? '▼' : '▲'}</span>
                                    </div>
                                  </button>
                                  <div className="flex items-center gap-2 mx-3">
                                    <button onClick={() => setFirmaOdemeGecmisi(getFirmaTumRezervasyonlar(grup.firmaId))} className="px-3 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all active:scale-95 flex-shrink-0">
                                      🧾 Ödemeler
                                    </button>
                                    <button onClick={() => setFirmaOdemeModal(grup)} className="px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-[9px] font-black uppercase tracking-widest transition-all active:scale-95 flex-shrink-0 shadow-lg shadow-violet-100">
                                      💳 Öde
                                    </button>
                                  </div>
                                </div>
                                {!grupKapali && <div className="divide-y divide-violet-50">
                                  {grup.rezervasyonlar.map(b => {
                                    var tetiklendi = tetiklenenIdler?.has(b.rezervasyonId);
                                    return (
                                      <div key={b.rezervasyonId} className={`flex items-center px-6 py-4 transition-all group ${tetiklendi ? 'bg-orange-50' : 'bg-white hover:bg-violet-50/30'}`}>
                                        <div className="flex-[2] text-left">
                                          <p className="text-sm font-black text-gray-900 tracking-tighter leading-none mb-1">{b.adSoyad || '—'}</p>
                                          <p className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.1em]">Rez. #{b.rezNo}</p>
                                        </div>
                                        <div className="w-24 text-center">
                                          <span className="text-[10px] font-black text-indigo-500 bg-indigo-50 px-3 py-1.5 rounded-xl uppercase tracking-widest">{b.odaNo}</span>
                                        </div>
                                        <div className="flex-1 hidden lg:block text-left italic text-[10px] font-bold text-violet-400">{grup.firmaAd}</div>
                                        <div className="w-32 text-left">
                                          <p className="text-[10px] font-black text-gray-700 tracking-tight leading-none italic">
                                            {!b.cikisTarihi || b.cikisTarihi.startsWith('0001') ? 'BELİRSİZ / AÇIK' : fmtTarih(b.cikisTarihi)}
                                          </p>
                                          <p className="text-[8px] font-bold text-gray-400 uppercase mt-1">Check-OUT Tarihi</p>
                                        </div>
                                        <div className="w-36 text-right">
                                          <p className={`text-sm font-black tracking-tighter ${b.kalanBakiye > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{fmtPara(b.kalanBakiye)} TL</p>
                                        </div>
                                        <div className="w-40 text-center"><StatusBadge durum={b.odemeDurumu} /></div>
                                        <div className="w-32 flex justify-center">
                                          <button onClick={() => setAlarmModal(b)} className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${tetiklendi ? 'bg-orange-500 text-white shadow-lg animate-pulse' : b.alarmTarihi ? 'bg-indigo-50 text-indigo-500 hover:bg-indigo-100' : 'bg-gray-50 text-gray-300 hover:bg-indigo-50 hover:text-indigo-400'}`}>🔔</button>
                                        </div>
                                        <div className="w-48 flex justify-end gap-2 pr-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                          <button onClick={() => handleDetay(b)} className="w-9 h-9 bg-gray-900 hover:bg-black text-white rounded-xl flex items-center justify-center transition-all active:scale-95">📄</button>
                                          <button onClick={() => setSilModal(b.rezervasyonId)} className="w-9 h-9 bg-gray-100 hover:bg-rose-50 hover:text-rose-600 text-gray-400 rounded-xl flex items-center justify-center transition-all active:scale-95 italic font-black">🗑</button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
              </div>

              {/* Mobile Card Layout */}
              <div className="md:hidden space-y-6">
                  {yukleniyor ? (
                      <LoadingSpinner fullPage text="Yükleniyor..." />
                  ) : filtrelenmis.length === 0 ? (
                      <div className="py-20 text-center bg-gray-50 rounded-[2rem] border-2 border-dashed border-gray-100">
                         <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Kayıt Bulunamadı</p>
                      </div>
                  ) : (
                    <>
                      {/* Bireysel */}
                      {normalBakiyeler.length > 0 && (
                        <div className="space-y-4">
                          {firmaGruplari.length > 0 && (
                            <div className="flex items-center gap-3 px-2 pt-2 pb-1">
                              <div className="h-px flex-1 bg-slate-100" />
                              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Bireysel</span>
                              <div className="h-px flex-1 bg-slate-100" />
                            </div>
                          )}
                          {normalBakiyeler.map(b => (
                            <BakiyeKart key={b.rezervasyonId} b={b} tetiklendi={tetiklenenIdler?.has(b.rezervasyonId)} onOdeme={setOdemeModal} onDetay={handleDetay} onSil={setSilModal} onAlarm={setAlarmModal} />
                          ))}
                        </div>
                      )}
                      {/* Kurumsal */}
                      {firmaGruplari.length > 0 && (
                        <div className="space-y-6 mt-4">
                          <div className="flex items-center gap-3 px-2">
                            <div className="h-px flex-1 bg-violet-100" />
                            <span className="text-[10px] font-black text-violet-500 uppercase tracking-widest">Kurumsal</span>
                            <div className="h-px flex-1 bg-violet-100" />
                          </div>
                          {firmaGruplari.map(grup => {
                            var grupToplam = grup.rezervasyonlar.reduce((s,b) => s+(b.kalanBakiye||0), 0);
                            var grupKapali = kapaliGruplar.has(grup.firmaId);
                            return (
                              <div key={grup.firmaId} className="border-2 border-violet-100 rounded-[2rem] overflow-hidden">
                                <div className="bg-violet-50">
                                  <button onClick={() => toggleGrup(grup.firmaId)} className="w-full px-5 py-4 flex items-center justify-between hover:bg-violet-100 transition-colors">
                                    <div className="flex items-center gap-2">
                                      <span>🏢</span>
                                      <div className="text-left">
                                        <p className="text-sm font-black text-violet-800 uppercase tracking-tight">{grup.firmaAd}</p>
                                        <p className="text-[9px] font-bold text-violet-400 uppercase tracking-widest">{grup.rezervasyonlar.length} rezervasyon</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <p className={`text-base font-black tracking-tighter ${grupToplam > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{fmtPara(grupToplam)} TL</p>
                                      <span className="text-violet-400 font-black">{grupKapali ? '▼' : '▲'}</span>
                                    </div>
                                  </button>
                                  <div className="px-4 pb-3 flex gap-2">
                                    <button onClick={() => setFirmaOdemeGecmisi(getFirmaTumRezervasyonlar(grup.firmaId))}
                                      className="flex-1 h-11 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95">
                                      🧾 Ödemeler
                                    </button>
                                    <button onClick={() => setFirmaOdemeModal(grup)}
                                      className="flex-[2] h-11 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 shadow-lg shadow-violet-100">
                                      💳 Öde
                                    </button>
                                  </div>
                                </div>
                                {!grupKapali && (
                                  <div className="space-y-3 p-3">
                                    {grup.rezervasyonlar.map(b => (
                                      <BakiyeKart key={b.rezervasyonId} b={b} tetiklendi={tetiklenenIdler?.has(b.rezervasyonId)} onOdeme={null} onDetay={handleDetay} onSil={setSilModal} onAlarm={setAlarmModal} />
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}

                  {filtrelenmis.length > 0 && (
                    <div className="bg-gradient-to-br from-rose-500 to-rose-700 rounded-[2.5rem] p-8 text-white shadow-xl shadow-rose-100 flex items-center justify-between mt-8 relative overflow-hidden group">
                        <div className="absolute right-[-20px] top-[-20px] text-7xl opacity-10 group-hover:rotate-12 transition-transform italic font-black">SUM</div>
                        <div>
                             <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-70 mb-1 leading-none">Toplam Borç</p>
                             <p className="text-3xl font-black tracking-tighter leading-none">{fmtPara(toplamBakiye)} <span className="text-xs uppercase opacity-70 italic font-black">TL</span></p>
                        </div>
                        <div className="w-14 h-14 bg-white/20 rounded-[1.5rem] flex items-center justify-center text-2xl shadow-inner backdrop-blur-sm">💵</div>
                    </div>
                  )}
              </div>

              <Pagination toplam={filtrelenmis.length} sayfaBasi={sayfaBasi} sayfaBasiDegistir={setSayfaBasi} aktifSayfa={aktifSayfa} sayfaDegistir={setAktifSayfa} />
          </div>
      </div>

      {/* Styles & Modals */}
      {alarmModal && (
        <AlarmModal bakiye={alarmModal} onClose={() => setAlarmModal(null)} onKaydedildi={() => fetchBakiyeler()} onTemizle={alarmTemizle} />
      )}
      {odemeModal && (
        <OdemeAlModal bakiye={odemeModal} onClose={() => setOdemeModal(null)} onKaydedildi={() => fetchBakiyeler()} />
      )}
      {firmaOdemeModal && (
        <FirmaOdemeModal grup={firmaOdemeModal} onClose={() => setFirmaOdemeModal(null)} onKaydedildi={() => fetchBakiyeler()} />
      )}
      {firmaOdemeGecmisi && (
        <FirmaOdemeGecmisiModal grup={firmaOdemeGecmisi} onClose={() => setFirmaOdemeGecmisi(null)} onKaydedildi={() => fetchBakiyeler()} />
      )}
      {uyariModal && <CheckOutUyariModal onClose={() => setUyariModal(false)} />}
      {detayId !== null && (
        <RezervasyonDetayModal rezervasyonId={detayId} onClose={() => setDetayId(null)} onGuncelle={() => fetchBakiyeler()} />
      )}
      {silModal && (
        <SilOnayModal onClose={() => setSilModal(null)} onSil={async () => {
            try {
              await apiRequest('rezervasyonlar/' + silModal, 'DELETE');
              setSilModal(null);
              fetchBakiyeler();
            } catch (e) { alert(e?.message || 'Silme başarısız.'); }
          }}
        />
      )}

      {/* ── Alt Bilgi ── */}
      <UsageInfo
        id="kasa-bakiyeler"
        color="violet"
        title="Nasıl Kullanılır?"
        description="Bakiye durumu sayfası, otelinizde konaklaması devam eden veya ayrılmış ancak ödemesi eksik misafirleri (Açık Hesap) anlık olarak gösterir."
        scenarios={[
          { title: "Tahsilat Alarmları", content: "Ödeme bekleyen misafirler için alarm kurarak belirlenen tarihlerde sistemin size hatırlatma yapmasını sağlayabilirsiniz." },
          { title: "Hızlı Tahsilat", content: "Misafir kartındaki Nakit veya Kredi Kartı butonlarıyla saniyeler içinde kasaya para girişi yapabilirsiniz." },
          { title: "Durum Filtreleme", content: "Sadece ödeme bekleyenleri veya tüm borçlu misafirleri yukarıdaki filtre paneli ile anında listeleyebilirsiniz." }
        ]}
      />

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
      `}</style>
    </div>
  );
}
